// Gap Analysis service for multi-LLM legal document review

const OPENROUTER_API_BASE = 'https://openrouter.ai/api/v1/chat/completions';
export interface AnalysisModelConfig {
  id: string; // openrouter model id
  label: string; // display name
  reviewerId: 'A' | 'B' | 'C';
}
const GAP_ANALYSIS_SYSTEM_PROMPT = `Legal Gap Analysis Workflow — Instructions

Overview
This project conducts structured legal gap analyses of HR policies and employment agreements for Canadian organizations. Work proceeds in three steps for each document. Do not skip steps or combine them unless explicitly instructed.

Step 1 — Gap Analysis
When the user uploads or pastes a document and asks for a gap analysis, produce a structured analysis with the following format for each issue:
- Issue number and title — concise description of the gap
- Section / Location — exact section heading and paragraph reference from the document
- Issue and Risks — explain what the gap is, why it matters legally, and what the specific risk is if it is not addressed. Be specific about applicable Canadian legislation, case law principles (e.g., Waksdale, contra proferentem, constructive dismissal), and regulatory bodies (e.g., Office of the Privacy Commissioner, Ontario Human Rights Tribunal) where relevant
- Draft — proposed replacement or addition language, with all fillable fields formatted as [Question in plain language?]-xxxx, integrating seamlessly into the surrounding text

Formatting rules for Step 1:
- Order issues in the same sequence as they appear in the document
- Do not flag unfilled template fields as gaps — they are intentional placeholders
- Where multiple gaps affect the same section, address them together in one issue entry rather than separately to avoid repetition
- Reference the specific paragraph or sentence being addressed, not just the section heading

Step 2 — Consolidated Analysis
When the user provides a supplementary analysis from another source, produce a consolidated analysis that:
- Merges both analyses into a single numbered list
- Eliminates redundancy — if the same gap is identified in both analyses, address it once using the stronger or more complete treatment
- Maintains document sequence order
- Follows the same formatting rules as Step 1
- Flags any conflicts or material differences between the two analyses where they exist

Step 3 — Revised Word Document
When the user attaches the original Word document and asks for Step 3, produce a revised Word document with the following characteristics:

Tracked changes:
- Apply all changes from the consolidated analysis directly to the original document as native Word tracked changes
- Deletions appear as red strikethrough
- Insertions appear as blue underline
- New paragraphs with no original counterpart appear entirely in blue underline
- Do not produce a separate issue-by-issue document — changes are applied inline to the original

Comments:
- Attach a Word comment to each tracked change explaining the issue and the legal risk it addresses
- Comments should be concise but substantive — one to three sentences identifying the specific legal exposure and why the change resolves it
- Use "Claude" as the comment author

Variable fields:
- Every fillable field — both those carried over from the original document and new ones introduced by the tracked changes — must be highlighted in yellow
- Fields follow the format [Question in plain language?]-xxxx or [Question]-NNNN where NNNN is an existing field number

Technical requirements:
- Unpack the original .docx, edit the XML directly, and repack — do not use docx-js to recreate the document from scratch
- All tracked changes must be valid OOXML — w:del and w:ins must be siblings of w:r at the paragraph level, never nested inside w:r
- Validate the output before presenting it
- If validation fails, fix the XML errors before presenting the file

Formatting standards across all steps
- Fields are always phrased as questions: [What is the effective date of this policy?]-xxxx
- New fields introduced by the analysis use -xxxx; existing fields from the original document retain their original number
- Draft language integrates fields seamlessly into sentences — fields are never standalone labels
- Do not use bullet points or numbered lists in the Issue and Risks narrative — write in prose
- Do not use italics in drafted policy language

Jurisdiction
All analysis is conducted in the Canadian employment law context, with equal reference to all Canadian common-law jurisdictions. No single province is treated as the primary baseline. Where provincial standards differ materially, those differences are identified within the relevant issue. Where a principle is common across all common-law provinces, it may be stated without province-specific attribution. The Supreme Court of Canada is the primary jurisprudential reference; provincial appellate decisions are cited where they are the leading authority on a point that differs between provinces.

Applicable provincial equivalents where multi-province scope is indicated:
- The Supreme Court of Canada and Ontario Court of Appeal employment law jurisprudence
- PIPEDA and applicable provincial privacy legislation where personal information is involved
- The Canadian Human Rights Act where federal jurisdiction is indicated
- Where a policy applies across multiple provinces, note jurisdiction-specific differences where they are material

Recurring gaps to always check for, regardless of document type:
- No version control (effective date, reviewed date, next review date)
- No acknowledgement requirement
- Scope excludes Quebec without explanation or alternative framework
- Accommodation clause omits undue hardship standard or cooperation obligation
- Enforcement clause permits discipline without prior investigation
- "As amended from time to time" applied to Company policies without a notice requirement
- Complaint or grievance mechanism absent
- Related policies list incomplete
- Legal basis footnote unpopulated or too generic
- No policy review obligation with named owner and frequency
- Defined terms do not align with or account for variations in statutory terminology across jurisdictions (e.g., "meal period" vs. "meal break" vs. "rest period" vs. "eating period" — terminology varies by province and under the Canada Labour Code)

What not to do:
- Do not flag unfilled template fields as gaps
- Do not repeat the same gap in two different issue entries
- Do not produce Step 3 output using docx-js — always edit the original XML directly
- Do not present a Step 3 document that has failed validation
- Do not combine steps unless the user explicitly asks`;
const SYNTHESIS_SYSTEM_PROMPT = `You are a senior legal analyst synthesizing multiple AI-generated gap analysis reports. You have received three independent reviews (A, B, C) of the same legal document.

Your task is to:
1. **Identify consensus**: Gaps mentioned by multiple reviewers (highest priority)
2. **Highlight unique insights**: Important findings mentioned by only one reviewer
3. **Resolve conflicts**: Where reviewers disagree, provide balanced perspective
4. **Prioritize actions**: Create a ranked list of recommended actions
5. **Provide executive summary**: 2-3 sentence overview of key findings

Structure your synthesis clearly with sections for:
- Executive Summary
- High-Priority Consensus Issues
- Notable Unique Findings
- Recommended Action Plan

Format your response using clear Markdown: use ## for section headings, ### for sub-sections, **bold** for emphasis, and numbered or bullet lists for action items.

Be concise, actionable, and focus on the most critical legal gaps.`;
export interface AnalysisResult {
  reviewerId: 'A' | 'B' | 'C' | 'Synthesis';
  modelLabel: string;
  modelId: string;
  content: string;
  timestamp: Date;
  error?: string;
}

/**
 * Run gap analysis on document text using specified model.
 */
export async function analyzeDocument(documentText: string, model: AnalysisModelConfig, apiKey: string): Promise<AnalysisResult> {
  const userContent = `Please analyze the following legal document for gaps, missing clauses, and risk exposure:\n\n${documentText}`;

  // DEBUG: log prompts being sent to the model
  console.groupCollapsed(`[GapAnalysis] → ${model.label} (${model.id}) — reviewer ${model.reviewerId}`);
  console.log('SYSTEM PROMPT:\n', GAP_ANALYSIS_SYSTEM_PROMPT);
  console.log('USER PROMPT:\n', userContent);
  console.log('Document length (chars):', documentText.length);
  console.groupEnd();
  try {
    const response = await fetch(OPENROUTER_API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'LawVo Gap Analysis'
      },
      body: JSON.stringify({
        model: model.id,
        messages: [{
          role: 'system',
          content: GAP_ANALYSIS_SYSTEM_PROMPT
        }, {
          role: 'user',
          content: userContent
        }],
        temperature: 0.3,
        max_tokens: 4000
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `API error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const content = data.choices?.[0]?.message?.content || '';
    return {
      reviewerId: model.reviewerId,
      modelLabel: model.label,
      modelId: model.id,
      content,
      timestamp: new Date()
    };
  } catch (error) {
    return {
      reviewerId: model.reviewerId,
      modelLabel: model.label,
      modelId: model.id,
      content: '',
      timestamp: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Synthesize multiple analysis results into final recommendation.
 * The synthesis model MUST be provided by the caller — no hardcoded defaults.
 */
export async function synthesizeAnalyses(documentText: string, analyses: AnalysisResult[], apiKey: string, synthesisModel: {
  id: string;
  label: string;
}): Promise<AnalysisResult> {
  try {
    const reviewsBlock = analyses.map((a, idx) => `**Review ${String.fromCharCode(65 + idx)} (${a.modelLabel}):**\n${a.content}`).join('\n\n');
    const synthesisPrompt = `I have ${analyses.length} independent gap analysis reviews of a legal document. Please synthesize these into a cohesive final analysis.

${reviewsBlock}

Please provide a synthesized analysis that identifies consensus issues, unique insights, and prioritized recommendations.`;

    // DEBUG: log prompts being sent to the synthesis model
    console.groupCollapsed(`[GapAnalysis Synthesis] → ${synthesisModel.label} (${synthesisModel.id})`);
    console.log('SYSTEM PROMPT:\n', SYNTHESIS_SYSTEM_PROMPT);
    console.log('USER PROMPT:\n', synthesisPrompt);
    console.log('Number of reviews:', analyses.length);
    console.groupEnd();
    const response = await fetch(OPENROUTER_API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'LawVo Gap Analysis Synthesis'
      },
      body: JSON.stringify({
        model: synthesisModel.id,
        messages: [{
          role: 'system',
          content: SYNTHESIS_SYSTEM_PROMPT
        }, {
          role: 'user',
          content: synthesisPrompt
        }],
        temperature: 0.4,
        max_tokens: 5000
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `API error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const content = data.choices?.[0]?.message?.content || '';
    return {
      reviewerId: 'Synthesis',
      modelLabel: synthesisModel.label,
      modelId: synthesisModel.id,
      content,
      timestamp: new Date()
    };
  } catch (error) {
    return {
      reviewerId: 'Synthesis',
      modelLabel: synthesisModel.label,
      modelId: synthesisModel.id,
      content: '',
      timestamp: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// LocalStorage keys for API key (kept for backward compatibility)
const API_KEY_STORAGE_KEY = 'lawvo_openrouter_api_key';
export function getStoredApiKey(): string | null {
  return localStorage.getItem(API_KEY_STORAGE_KEY);
}
export function setStoredApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE_KEY, key);
}